import { ClusterVmhaStatus, VMHAStatusDetails } from 'api-client/model'
import { CLUSTER_HOST_SCHEDULING } from 'app/constants'
import { AppSelector } from 'app/store'
import getDataSelector from 'core/utils/getDataSelector'
import { createSharedSelector } from 'core/utils/selectorHelpers'
import DataKeys from 'k8s/DataKeys'
import { resmgrHostsSelector } from 'openstack/components/infrastructure/selectors'
import { getAggregatedUsage, VMHADisplayStatus } from './helpers'
import { ClusterHostGroup, IClusterHostGroup } from './model'

interface ClusterRoles {
  hypervisor: any[]
}

interface RolesByCluster {
  [Name: string]: ClusterRoles
}

export const clusterHostGroupsSelectorSimple: AppSelector<ClusterHostGroup[]> = createSharedSelector(
  getDataSelector<DataKeys.ClusterHostGroups>(DataKeys.ClusterHostGroups),
  (clusterHostGroups): ClusterHostGroup[] => {
    return clusterHostGroups.map((clusterHostGroup: ClusterHostGroup) => clusterHostGroup)
  },
)

export const clusterHostGroupsSelector: AppSelector<IClusterHostGroup[]> = createSharedSelector(
  getDataSelector<DataKeys.ClusterHostGroups>(DataKeys.ClusterHostGroups),
  getDataSelector<DataKeys.ClusterVMHAStatus>(DataKeys.ClusterVMHAStatus),
  resmgrHostsSelector,
  (clusterHostGroups, clusterVMHAStatuses, resmgrHosts): IClusterHostGroup[] => {
    return clusterHostGroups.map((clusterHostGroup: ClusterHostGroup) => {
      const hosts = resmgrHosts.filter((host) => clusterHostGroup?.hostlist?.includes(host.id))
      const physicalNetworkLabels = hosts?.[0]?.physicalNetworkLabels || []

      const vmhaState =
        clusterVMHAStatuses?.find(
          (clusterVMHAStatus) => clusterVMHAStatus?.name === clusterHostGroup?.name,
        ) ?? ({} as VMHAStatusDetails)

      const vmhaStatus = (() => {
        if (!clusterHostGroup?.vmHighAvailability?.enabled) {
          return VMHADisplayStatus.DISABLED
        } else if (vmhaState?.vmha_status === ClusterVmhaStatus.Protected) {
          return VMHADisplayStatus.PROTECTED
        } else if (vmhaState?.vmha_status === ClusterVmhaStatus.Degraded) {
          return VMHADisplayStatus.DEGRADED
        } else if (vmhaState?.vmha_status === ClusterVmhaStatus.Unprotected) {
          return VMHADisplayStatus.NOT_PROTECTED
        } else {
          return VMHADisplayStatus.LOADING
        }
      })()

      return {
        ...clusterHostGroup,
        hosts,
        totalUsage: getAggregatedUsage(hosts),
        physicalNetworkLabels,
        vmhaStatus,
        vmhaState,
      }
    })
  },
)

export const activeRoleHostsByCluster: AppSelector<RolesByCluster> = createSharedSelector(
  resmgrHostsSelector,
  clusterHostGroupsSelector,
  (hosts, clusters) => {
    // In the future, will need for storage hosts too
    const activeHypervisors = hosts.filter(
      (host) => host?.schedulingStatus === CLUSTER_HOST_SCHEDULING.enabled,
    )
    const activeHypervisorIds = activeHypervisors.map((host) => host.id)
    return clusters.reduce((accum, cluster) => {
      return {
        ...accum,
        [cluster.name]: {
          hypervisor: (cluster?.hosts || [])?.filter((host) =>
            activeHypervisorIds.includes(host?.id),
          ),
        },
      }
    }, {})
  },
)

export const clusterVMHAStatusSelector: AppSelector<VMHAStatusDetails[]> = createSharedSelector(
  getDataSelector<DataKeys.ClusterVMHAStatus>(DataKeys.ClusterVMHAStatus),
  (clusterVMHAStatuses): VMHAStatusDetails[] => {
    return clusterVMHAStatuses.map((clusterVMHAStatus: VMHAStatusDetails) => {
      return {
        ...clusterVMHAStatus,
      }
    })
  },
)
